import React, { useState } from "react";
import { Toaster, toast } from "sonner";
import { Check, Star } from "lucide-react";
import { submitInquiry } from "../lib/api";
import { track } from "../lib/analytics";
import { COMPANY, PACKAGES, PACKAGE_PRICING, PACKAGE_PROCESS, SERVICES_INCLUDED } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import MotionMedia from "../components/MotionMedia";
import { videoJsonLd } from "../lib/videoSchema";

const PROPERTY_SIZES = [
  { value: "studio", label: "Studio" },
  { value: "1br", label: "1 Bedroom" },
  { value: "2br", label: "2 Bedrooms" },
  { value: "3br", label: "3 Bedrooms" },
  { value: "villa", label: "Villa / Townhouse" },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  property_size: "",
  package_tier: "",
  location: "",
  message: "",
};

export default function HolidayHomes() {
  usePageMeta({
    title: "Holiday Home Furnishing Dubai | Airbnb Furniture Packages UAE",
    description:
      "Turnkey holiday home and Airbnb furnishing packages designed and delivered across the UAE — built to lift guest experience and rental income. Since 1993.",
    canonical: "https://timefurniture.ae/holiday-homes",
    ogImage: "/projects/palm-tower-1.webp",
    jsonLd: [
      videoJsonLd({
        key: "holiday-hero",
        name: "Holiday home furnishing in Dubai — from first visit to guest-ready",
        description:
          "The Time Furniture holiday-home process on film: consultation, measurement and design approval, the furnishing installation team at work, and finished guest-ready rooms.",
        seconds: 32,
      }),
      {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Holiday home furnishing packages",
      name: "Holiday Home Furnishing Dubai",
      provider: { "@id": "https://timefurniture.ae/#organization" },
      areaServed: [
        { "@type": "City", name: "Dubai" },
        { "@type": "City", name: "Sharjah" },
        { "@type": "Country", name: "United Arab Emirates" },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "AED",
        lowPrice: "34500",
        highPrice: "145000",
      },
    },
    ],
  });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePickTier = (tierKey) => {
    update("package_tier", tierKey);
    // Scroll to inquiry form
    document.getElementById("holiday-inquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({
        ...form,
        service: "Holiday Home Package",
        source_page: "/holiday-homes",
      });
      toast.success("Thank you. A holiday-home specialist will respond within 24 hours.");
      track.formSubmitSuccess({ form: "holiday_homes", service: "Holiday Home Package", source_page: "/holiday-homes" });
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="holiday-page" className="bg-[#FAF5EC]">
      <Toaster theme="light" position="top-center" />

      {/* HERO */}
      <section className="relative min-h-[85vh] -mt-20 flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <MotionMedia
            mediaKey="holiday-hero"
            alt="Holiday home furnishing film — consultation and measurement, furnishing installation team, and guest-ready rooms"
            mode="hero"
            aspectClass="h-full w-full"
            testid="holiday-hero-media"
            eager
            toggleClass="top-24 right-5 md:top-28 md:right-8"
          />
          <span
            aria-hidden="true"
            data-testid="holiday-hero-mobile-wordmark"
            className="sm:hidden pointer-events-none select-none absolute top-24 left-5 z-20 font-serif-display text-[13px] tracking-[0.3em] uppercase text-white opacity-20"
          >
            TIME · FURNITURE
          </span>
          <div className="absolute inset-0 bg-black/55 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FAF5EC] via-[#1C110C]/30 to-[#1C110C]/10 pointer-events-none" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-20 md:pb-32 w-full">
          <p className="mb-6 text-[11px] tracking-[0.28em] uppercase text-[#EDE1C8] font-medium">Holiday Home Packages · DTCM Compliant</p>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-[#FAF5EC] max-w-5xl">
            Holiday Home Furnishing in <em className="text-[#B8904E] not-italic">Dubai</em>.
          </h1>
          <p className="mt-6 font-serif-display text-2xl md:text-3xl text-[#EDE1C8] max-w-3xl">
            Rental-ready homes, move-in polished.
          </p>
          <p className="mt-8 max-w-xl text-[#E8DDC7] leading-relaxed text-base md:text-lg">
            All-inclusive onboarding packages for landlords and investors. From inspection to professional photoshoot — we deliver a photo-ready, guest-ready home on a fixed timeline.
          </p>

          {/* Benefits row */}
          <div
            data-testid="holiday-hero-benefits"
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] tracking-[0.24em] uppercase text-[#EDE1C8]"
          >
            <span className="flex items-center gap-2">
              <Check size={14} className="text-[#B8904E]" strokeWidth={2.4} />
              Airbnb-Ready Setup
            </span>
            <span className="flex items-center gap-2">
              <Check size={14} className="text-[#B8904E]" strokeWidth={2.4} />
              Premium Furnishing
            </span>
            <span className="flex items-center gap-2">
              <Check size={14} className="text-[#B8904E]" strokeWidth={2.4} />
              Turnkey Execution
            </span>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#packages"
              data-testid="holiday-cta-packages"
              className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
            >
              View Holiday Home Packages
            </a>
            <a
              href="#holiday-inquiry"
              data-testid="holiday-cta-enquire"
              className="inline-flex items-center gap-3 border border-[#6E1F2C] bg-[#FAF5EC]/90 text-[#6E1F2C] px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <Breadcrumbs
        testid="holiday-breadcrumbs"
        items={[
          { label: "Home", to: "/" },
          { label: "Holiday Homes" },
        ]}
      />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-28 md:pb-40 lg:pb-48">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20 md:mb-24 md:mb-20">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">How It Works</p>
            <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
              Five steps from key <br /> to <em className="text-[#6E1F2C] not-italic">first booking</em>.
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-[#7A6B5F] leading-relaxed">
            Every engagement follows the same disciplined process. You hand us the keys — we hand back a home that is styled, compliant, and ready to be listed on the night of our final visit.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[#6E1F2C]/10">
          {PACKAGE_PROCESS.map((p) => (
            <div key={p.n} className="bg-[#FAF5EC] p-8" data-testid={`process-${p.n}`}>
              <p className="font-serif-display text-4xl text-[#6E1F2C]">{p.n}</p>
              <p className="mt-4 font-serif-display text-xl text-[#1C110C] leading-tight">{p.t}</p>
              <p className="mt-3 text-sm text-[#7A6B5F] leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="border-t border-[#6E1F2C]/15 bg-[#FAF5EC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-40 lg:py-48">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 md:mb-16">
            <div>
              <p className="eyebrow mb-5">Three Tiers</p>
              <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight">Choose your package.</h2>
            </div>
            <p className="max-w-md text-sm text-[#7A6B5F] leading-relaxed">
              Each tier is fully DTCM-compliant. All packages can be tailored to your property, target guest segment, and brand.
            </p>
          </div>

          {/* Compact property-size selector — verified indicative ranges only */}
          <div
            data-testid="holiday-size-selector"
            className="mb-14 md:mb-20 border border-[#6E1F2C]/15 bg-white p-6 md:p-8"
          >
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-4">
              Quick check — your property size
            </p>
            <div className="flex flex-wrap gap-2">
              {PACKAGE_PRICING.map((row) => (
                <button
                  key={row.code}
                  type="button"
                  onClick={() => update("property_size", row.code)}
                  data-testid={`size-chip-${row.code}`}
                  className={`px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                    form.property_size === row.code
                      ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]"
                      : "bg-white text-[#6E1F2C] border-[#6E1F2C]/25 hover:border-[#6E1F2C]"
                  }`}
                >
                  {row.size}
                </button>
              ))}
            </div>
            {form.property_size && (() => {
              const row = PACKAGE_PRICING.find((r) => r.code === form.property_size);
              if (!row) return null;
              return (
                <div className="mt-5" data-testid="size-selector-result">
                  {row.from ? (
                    <>
                      <p className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">
                        AED {row.from.toLocaleString()} – {row.to.toLocaleString()}
                        <span className="ml-2 text-sm text-[#7A6B5F] font-sans">excl. 5% VAT</span>
                      </p>
                      <p className="mt-2 text-xs text-[#7A6B5F] italic">
                        Indicative range across all specifications for this size — not a tier base
                        price. A tier-specific quotation is required and depends on your unit,
                        existing appliances and chosen finishes.
                      </p>
                    </>
                  ) : (
                    <p className="font-serif-display text-2xl md:text-3xl text-[#6E1F2C]">
                      Custom quote — villas &amp; townhouses are quoted after inspection.
                    </p>
                  )}
                  <a
                    href="#holiday-inquiry"
                    data-testid="size-selector-quote-link"
                    className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#6E1F2C] border-b border-[#6E1F2C]/40 pb-1 hover:text-[#56151F]"
                  >
                    Use this in my quote →
                  </a>
                </div>
              );
            })()}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {PACKAGES.map((p) => (
              <article
                key={p.key}
                data-testid={`package-card-${p.key}`}
                className={`relative flex flex-col transition-all ${
                  p.key === "premium"
                    ? "bg-[#1C110C] text-[#FAF5EC] border-2 border-[#D4A963] p-10 md:p-12 md:scale-[1.04] md:-translate-y-3 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.35)] ring-1 ring-[#D4A963]/30 ring-offset-4 ring-offset-[#FAF5EC]"
                    : p.featured
                    ? "bg-white border border-[#B8904E] p-8 md:p-10"
                    : "bg-white border border-[#6E1F2C]/15 p-8 md:p-10"
                }`}
              >
                {p.key === "premium" && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D4A963] text-[#1C110C] text-[10px] tracking-[0.32em] uppercase px-5 py-2 flex items-center gap-2 whitespace-nowrap shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)]">
                    <Star size={11} fill="currentColor" /> Premium
                  </span>
                )}
                {p.featured && p.key !== "premium" && (
                  <span className="absolute -top-3 left-8 bg-[#6E1F2C] text-[#FAF5EC] text-[10px] tracking-[0.28em] uppercase px-3 py-1 flex items-center gap-2">
                    <Star size={10} fill="currentColor" /> Most Chosen
                  </span>
                )}
                <div className="overflow-hidden mb-6 h-48 img-zoom-parent">
                  <img src={p.image} alt={p.imageAlt || `${p.name} holiday home package by Time Furniture`} className="w-full h-full object-cover img-zoom" loading="lazy" />
                </div>
                <p className={`text-[10px] tracking-[0.32em] uppercase ${p.key === "premium" ? "text-[#D4A963]" : "text-[#6E1F2C]"}`}>
                  {p.fit}
                </p>
                <h3 className={`font-serif-display text-4xl mt-3 ${p.key === "premium" ? "text-[#FAF5EC]" : "text-[#1C110C]"}`}>
                  {p.name}
                </h3>
                <p className={`mt-2 text-sm italic ${p.key === "premium" ? "text-[#EDE1C8]" : "text-[#6E1F2C]"}`}>
                  {p.tagline}
                </p>

                {p.benefit && (
                  <p className={`mt-4 text-sm leading-relaxed ${p.key === "premium" ? "text-[#E8DDC7]" : "text-[#3E2A22]"}`}>
                    {p.benefit}
                  </p>
                )}

                <ul className={`mt-6 space-y-3 text-sm ${p.key === "premium" ? "text-[#FAF5EC]" : "text-[#1C110C]"}`}>
                  {p.highlights.map((h) => (
                    <li key={h} className="flex gap-3 items-start">
                      <Check
                        size={14}
                        className={`mt-1 shrink-0 ${p.key === "premium" ? "text-[#D4A963]" : "text-[#6E1F2C]"}`}
                        strokeWidth={2.4}
                      />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <details className={`mt-6 pt-5 border-t ${p.key === "premium" ? "border-[#D4A963]/25" : "border-[#6E1F2C]/15"}`}>
                  <summary className={`cursor-pointer text-[11px] tracking-[0.28em] uppercase hover:text-[#D4A963] transition-colors ${p.key === "premium" ? "text-[#EDE1C8]" : "text-[#7A6B5F]"}`}>
                    View full inclusions →
                  </summary>
                  <div className="mt-5 space-y-5">
                    {Object.entries(p.inclusions).map(([cat, items]) => (
                      <div key={cat}>
                        <p className={`text-[10px] tracking-[0.28em] uppercase mb-2 ${p.key === "premium" ? "text-[#D4A963]" : "text-[#6E1F2C]"}`}>
                          {cat}
                        </p>
                        <ul className={`text-xs space-y-1 leading-relaxed ${p.key === "premium" ? "text-[#E8DDC7]" : "text-[#7A6B5F]"}`}>
                          {items.map((it) => (
                            <li key={it}>· {it}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </details>

                <button
                  onClick={() => handlePickTier(p.key)}
                  data-testid={`package-cta-${p.key}`}
                  className={`mt-8 py-4 text-[11px] tracking-[0.28em] uppercase transition-colors ${
                    p.key === "premium"
                      ? "bg-[#D4A963] text-[#1C110C] hover:bg-[#c9a166]"
                      : "bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F]"
                  }`}
                >
                  Request {p.name} Quote
                </button>
              </article>
            ))}
          </div>

          {/* Premium Upgrade pricing — bold, easy to read */}
          <div className="mt-24 md:mt-32">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#B8904E] mb-4">
                Premium Upgrade · Add-on to Standard
              </p>
              <h3 className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#1C110C] leading-[1.05]">
                Upgrade to <em className="text-[#6E1F2C] not-italic">Premium</em> — 85″ TV, premium linens, balcony &amp; coffee machine.
              </h3>
            </div>
            <div
              data-testid="premium-upgrade-table"
              className="grid grid-cols-2 md:grid-cols-4 border border-[#6E1F2C]/15 max-w-5xl mx-auto"
            >
              {[
                { size: "Studio", add: "+20,000" },
                { size: "1 Bedroom", add: "+20,000" },
                { size: "2 Bedrooms", add: "+20,000" },
                { size: "3 Bedrooms", add: "+25,000" },
              ].map((p, i) => (
                <div
                  key={p.size}
                  data-testid={`premium-upgrade-row-${p.size.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-5 py-12 md:py-16 text-center bg-white ${
                    i < 3 ? "border-r border-[#6E1F2C]/15" : ""
                  } ${i < 2 ? "border-b md:border-b-0 border-[#6E1F2C]/15" : ""}`}
                >
                  <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
                    {p.size}
                  </p>
                  <p className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1C110C] leading-none font-medium">
                    {p.add}
                  </p>
                  <p className="mt-3 text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">AED</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-[#7A6B5F] mt-6 italic">
              Premium adds 85″ Smart TV, premium curtains &amp; linens, balcony + swing + lounge set, and coffee machine.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING TABLE */}
      <section className="border-t border-[#6E1F2C]/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-40 lg:py-48">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-5">Indicative Pricing</p>
              <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
                Transparent <em className="text-[#6E1F2C] not-italic">ranges</em>.
              </h2>
              <p className="mt-6 text-sm text-[#7A6B5F] leading-relaxed">
                Final quotations are tailored to your property size, existing appliances, and chosen finishes. Custom packages are available for villas and townhouses.
              </p>
              <p className="mt-4 text-xs text-[#9A8A7F] italic">All prices in AED. Excludes 5% VAT.</p>
            </div>
            <div className="lg:col-span-7 border border-[#6E1F2C]/15">
              {PACKAGE_PRICING.map((row, i) => (
                <div
                  key={row.code}
                  data-testid={`pricing-row-${row.code}`}
                  className={`grid grid-cols-[1fr_auto] gap-6 items-baseline px-6 md:px-8 py-6 ${
                    i !== PACKAGE_PRICING.length - 1 ? "border-b border-[#6E1F2C]/15" : ""
                  }`}
                >
                  <div>
                    <p className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">{row.size}</p>
                    {row.note && <p className="text-xs text-[#7A6B5F] mt-1">{row.note}</p>}
                  </div>
                  <div className="text-right">
                    {row.from ? (
                      <>
                        <p className="font-serif-display text-2xl md:text-3xl text-[#6E1F2C]">
                          {row.from.toLocaleString()} – {row.to.toLocaleString()}
                        </p>
                        <p className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">AED</p>
                      </>
                    ) : (
                      <p className="font-serif-display text-2xl md:text-3xl text-[#6E1F2C]">On request</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="border-t border-[#6E1F2C]/15 bg-[#FAF5EC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-40 lg:py-48">
          <p className="eyebrow mb-5">Every Package Includes</p>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight mb-20 md:mb-24 md:mb-20">End-to-end service, no hidden steps.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {SERVICES_INCLUDED.map((s) => (
              <div key={s} className="flex gap-3 items-start border-b border-[#6E1F2C]/15 pb-4">
                <Check size={14} className="text-[#6E1F2C] mt-1 shrink-0" />
                <p className="text-sm text-[#1C110C]">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOBHA CREST CASE — real delivered holiday home, near the decision area */}
      <section
        data-testid="holiday-case-sobha"
        className="border-t border-[#6E1F2C]/15 bg-[#F2E9D8]"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <img
                src="/projects/sobha-crest-holiday-home-living-room.webp"
                alt="Sobha Crest one-bedroom holiday home living room — turnkey furnishing by Time Furniture"
                loading="lazy"
                className="w-full h-[360px] md:h-[440px] object-cover"
              />
            </div>
            <div className="lg:col-span-6">
              <p className="eyebrow mb-5">Delivered Project · Sobha Crest, Dubai</p>
              <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
                A one-bedroom holiday home,{" "}
                <em className="text-[#6E1F2C] not-italic">guest-ready in 3 working days</em>.
              </h2>
              <p className="mt-6 text-[#3E2A22] leading-[1.8] text-base md:text-[17px]">
                Complete turnkey fit-out of a one-bedroom holiday home apartment at Sobha
                Crest, Dubai — delivered, furnished, styled and guest-ready. Tan linen
                sectional with marble coffee table, oak veneer media console with feature
                pendant, and a soft cream upholstered master bedroom with brass chandelier.
              </p>
              <p className="mt-4 text-xs text-[#7A6B5F] italic">
                Timeline shown is for this specific project — every delivery schedule is
                confirmed per property after inspection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="holiday-inquiry" className="border-t border-[#6E1F2C]/15 bg-[#FAF5EC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-40 lg:py-48">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-5">Request a Quote</p>
              <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
                Tell us about your <em className="text-[#6E1F2C] not-italic">property</em>.
              </h2>
              <p className="mt-6 text-sm text-[#7A6B5F] leading-relaxed">
                Share a few details and we'll respond within 24 hours with an indicative quotation and next steps.
              </p>
              <div className="mt-10 border-t border-[#6E1F2C]/15 pt-8 text-sm text-[#7A6B5F] space-y-2">
                <p>Prefer voice?</p>
                <p className="text-[#1C110C]">Dubai · +971 4 269 3310</p>
                <p className="text-[#1C110C]">Sharjah · +971 6 559 1935</p>
              </div>
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="holiday-form"
              className="lg:col-span-7 border border-[#6E1F2C]/15 p-8 md:p-10 bg-white"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Full Name *" id="h-name" value={form.name} onChange={(v) => update("name", v)} testid="h-input-name" />
                <Field label="Email *" id="h-email" type="email" value={form.email} onChange={(v) => update("email", v)} testid="h-input-email" />
                <Field label="Phone" id="h-phone" value={form.phone} onChange={(v) => update("phone", v)} testid="h-input-phone" />
                <Field label="Property Location" id="h-loc" value={form.location} onChange={(v) => update("location", v)} testid="h-input-location" placeholder="e.g. Dubai Marina" />

                <div>
                  <label htmlFor="h-size" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">Property Size</label>
                  <select
                    id="h-size"
                    data-testid="h-input-size"
                    value={form.property_size}
                    onChange={(e) => update("property_size", e.target.value)}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold"
                  >
                    <option value="">Select size</option>
                    {PROPERTY_SIZES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="h-tier" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">Package Tier</label>
                  <select
                    id="h-tier"
                    data-testid="h-input-tier"
                    value={form.package_tier}
                    onChange={(e) => update("package_tier", e.target.value)}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold"
                  >
                    <option value="">Select tier</option>
                    {PACKAGES.map((p) => (
                      <option key={p.key} value={p.key}>{p.name}</option>
                    ))}
                    <option value="custom">Custom / Undecided</option>
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">Your Message *</label>
                <textarea
                  data-testid="h-input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={5}
                  className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold resize-none"
                  placeholder="Anything relevant — timeline, existing furniture, unit handover date..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="holiday-submit-btn"
                className="mt-8 inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-10 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60"
              >
                {loading ? "Sending..." : "Request My Quote"}
              </button>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(
                  `Hi Time Furniture, I'm interested in holiday home furnishing.${
                    form.property_size ? ` Property: ${(PROPERTY_SIZES.find((s) => s.value === form.property_size) || {}).label || form.property_size}.` : ""
                  }${
                    form.package_tier ? ` Tier: ${(PACKAGES.find((p) => p.key === form.package_tier) || {}).name || form.package_tier}.` : ""
                  } I found you from the website.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="holiday-whatsapp-prefill"
                className="mt-8 ml-0 sm:ml-3 inline-flex items-center gap-3 border border-[#6E1F2C]/40 text-[#6E1F2C] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-colors"
              >
                WhatsApp Instead
              </a>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">
        {label}
      </label>
      <input
        id={id}
        type={type}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold placeholder:text-[#B3A598]"
      />
    </div>
  );
}
